import { getPopulatedIngredientGroups, productToBasketItem } from "./products";
import { isEmpty } from "lodash";
import { round } from "lodash/math";
import store from "../store";
import Vue from "vue";
import {
  annotateMenuVatBases,
  annotateProductVatBases,
  finalizeBasketVatDetails
} from "@/utils/vat";

export const COUPON_CATEGORIES = {
  AMOUNT_OFF: 1,
  PERCENT_OFF: 2,
  AMOUNT_OFF_CATEGORY: 3,
  PERCENT_OFF_CATEGORY: 4
};

export function isPricingTypeClassic(combo) {
  return combo && combo.pricing_type === "classic";
}

export function isPricingTypeFixed(combo) {
  return combo && combo.pricing_type === "fixed";
}

export function isPricingTypeSum(combo) {
  return combo && combo.pricing_type === "sum";
}

export function annotatePricesIngredientGroup(product, ingredient_group) {
  let rv = [];
  let included_count = ingredient_group.price_included_count;
  if (product.default_ingredients_included) {
    ingredient_group.ingredients.forEach(ing => {
      if (ing.default_count > 0) {
        included_count = Math.max(0, included_count - ing.default_count);
      }
    });
  }
  let ctr = 0;
  ingredient_group.ingredients.forEach(ing => {
    let price = 0.0;
    if (
      ing.count > ing.default_count ||
      !product.default_ingredients_included
    ) {
      for (
        let i = product.default_ingredients_included ? ing.default_count : 0;
        i < ing.count;
        i++
      ) {
        let bp = 0.0;
        ctr += 1;
        if (ctr > included_count) {
          bp = toCalculatable(ingredient_group.price_included_baseprice);
        }
        price += toCalculatable(ing.price) + bp;
      }
    }
    let v = { ...ing, totalPrice: price };
    rv.push(v);
  });
  return rv;
}

export function ingredientPrice(product, clazz_id) {
  let sum = 0.0;
  let igs = getPopulatedIngredientGroups(product);
  let clazzes = igs.map(ig => ig.id);
  if (typeof clazz_id !== "undefined") {
    clazzes = [clazz_id];
  }
  clazzes.forEach(clazz_id => {
    let ig = igs.find(ig => ig.id === clazz_id);
    if (!ig) return;
    let annotated_ingredients = annotatePricesIngredientGroup(product, ig);
    annotated_ingredients.forEach(ing => {
      sum += ing.totalPrice;
    });
  });
  return sum;
}

export function ingredientExtra(product) {
  let sum = 0.0;
  sum += ingredientPrice(product);
  if (product && product.selectedExtras) {
    product.selectedExtras.forEach(extra => {
      sum += toCalculatable(extra.offset_price);
    });
  }
  return sum;
}

export function comboPrice(combo, with_coupons = false, global_coupons = []) {
  let deposit = 0;
  let sum = toCalculatable(combo.price);
  if (combo.components[0].selected) {
    sum += toCalculatable(combo.components[0].selected.price);
  }
  combo.components.forEach((component, index) => {
    sum += ingredientExtra(component.selected);
    if (component.selected.selectedSize) {
      sum += toCalculatable(component.selected.selectedSize.offset_price);
    }
    if (index !== 0) {
      //don't count price for first component since it's price is the base price of the menu anyway.
      if (isPricingTypeFixed(combo) || isPricingTypeSum(combo)) {
        //always use attribute 'price' for pricing_type 'fixed' || 'sum'
        sum += Number(component.selected.price);
      } else {
        sum += Number(component.selected.menu_offset_price);
      }
    }
    if (component.selected.deposit) {
      deposit += toCalculatable(component.selected.deposit);
    }
  });
  if (with_coupons && !isPricingTypeFixed(combo) && !isEmpty(global_coupons)) {
    // isPricingTypeFixed(combo) aka splinter-deals is already reduced in price and should not be double promoted/reduced/discounted
    let percent_off_global = global_coupons.find(
      c => c.data.c === COUPON_CATEGORIES.PERCENT_OFF
    );
    if (percent_off_global) {
      sum -= sum * (percent_off_global.data.a / 100.0);
    }
  }
  if (deposit) {
    sum += deposit;
  }
  return sum;
}

export function comboDeposit(combo) {
  let deposit = 0;
  if (combo.components) {
    let deposits = combo.components.map(comp => comp.selected.deposit);
    deposits.forEach(d => {
      if (d) {
        deposit += toCalculatable(d);
      }
    });
  }
  return deposit;
}

export function isCombo(product) {
  return typeof product.selectedCombo !== "undefined" && product.selectedCombo;
}

/**
 * Provides basketItem price without deposit for products OR! sum of component prices in case of a menu
 * > menu with pricing_type 'classic': sum of product prices of menu components
 * > menu with pricing_type 'fixed':   sum of prices of menu components
 * @param product basketItem
 * @param with_coupons
 * @param global_coupons
 * @param index
 * @returns {number}
 */
export function productPriceWithoutDeposit(
  product,
  with_coupons,
  global_coupons,
  index
) {
  let price = productPrice(product, with_coupons, global_coupons, index);
  if (isCombo(product)) {
    price = price - comboDeposit(product);
  } else {
    if (product.deposit) {
      price = price - toCalculatable(product.deposit);
    }
  }
  return price;
}

export function productPrice(product, with_coupons, global_coupons, index) {
  if (isCombo(product)) {
    return comboPrice(product, with_coupons, global_coupons);
  }
  /*
   In context of fixed price menus (aka splinter deals) the product/component price attribute
     1. is additionally stored in attribute "original_price"
     2. and gets overwritten with the price stored at the menu component (should be refactored)
   @see setFixedPrice(combo) in helpers.js
  */
  let originalPrice = product.original_price
    ? product.original_price
    : product.price;
  let sum = toCalculatable(originalPrice);
  sum += ingredientExtra(product);
  if (product.selectedSize) {
    sum += toCalculatable(product.selectedSize.offset_price);
  }
  if (with_coupons) {
    let coupon;
    if (typeof index !== "undefined" && index === 0 && product.coupon)
      coupon = product.coupon;
    if (!coupon && global_coupons) {
      let percent_off_global = global_coupons.find(
        c => c.data.c === COUPON_CATEGORIES.PERCENT_OFF
      );
      if (percent_off_global) {
        coupon = {
          ...percent_off_global,
          data: {
            ...percent_off_global.data,
            c: COUPON_CATEGORIES.PERCENT_OFF_CATEGORY
          },
          deletable: false
        };
      }
    }
    if (coupon) {
      if (coupon.data.c === COUPON_CATEGORIES.AMOUNT_OFF_CATEGORY) {
        sum -= coupon.data.a;
      }
      if (coupon.data.c === COUPON_CATEGORIES.PERCENT_OFF_CATEGORY) {
        sum -= sum * (coupon.data.a / 100.0);
      }
    }
  }
  if (product.deposit) {
    sum = sum + toCalculatable(product.deposit);
  }
  return sum;
}

export function totalPriceCents(basket, global_coupons) {
  let sum = totalPriceRaw(basket, true, global_coupons);
  return parseInt("" + Math.round(100 * sum));
}

export function totalPriceValid(basket, global_coupons) {
  let sum = 0.0;
  basket.forEach(item => {
    for (let i = 0; i < item.count; i++) {
      sum += parseFloat(productPrice(item, true, global_coupons, i).toFixed(2));
    }
  });
  return basket.length > 0 && sum >= 0;
}

export function totalPriceRaw(basket, with_product_coupons, global_coupons) {
  let sum = 0.0;

  basket.forEach(item => {
    sum += parseFloat(
      productPrice(item, with_product_coupons, global_coupons, 0).toFixed(2)
    );
    sum += parseFloat(
      (
        (item.count - 1) *
        productPrice(item, with_product_coupons, global_coupons, 1)
      ).toFixed(2)
    );
  });
  if (global_coupons) {
    global_coupons.forEach(c => {
      if (c.data.c === COUPON_CATEGORIES.AMOUNT_OFF) {
        sum -= parseFloat(c.data.a.toFixed(2));
      }
      //All others are ignored.
    });
  }
  if (basket.length > 0) {
    if (sum < 0.01) sum = 0.01;
  }
  return parseFloat(sum.toFixed(2));
}

export function totalDeposit(basket) {
  return toPrintableCurrency(totalDepositRaw(basket).toFixed(2));
}

export function totalDepositRaw(basket) {
  let deposit = 0;
  basket.forEach(item => {
    if (typeof item.selectedCombo !== "undefined" && item.selectedCombo) {
      item.components.forEach(component => {
        if (component.selected.deposit) {
          deposit += item.count * toCalculatable(component.selected.deposit);
        }
      });
    } else {
      if (item.deposit) {
        deposit += item.count * toCalculatable(item.deposit);
      }
    }
  });
  return deposit;
}

export function subtotalPrice(basket, with_product_coupons, global_coupons) {
  let deposit = totalDepositRaw(basket);
  let sum = totalPriceRaw(basket, with_product_coupons, global_coupons);
  return toPrintableCurrency(sum - deposit);
}

export function totalPrice(basket, with_product_coupons, global_coupons) {
  return toPrintableCurrency(
    totalPriceRaw(basket, with_product_coupons, global_coupons)
  );
}

export function toCalculatable(floatRepresentation) {
  return parseFloat(floatRepresentation);
}

export function toPrintableCurrency(price) {
  let currentLocale = store.getters.LOCALE_CODE.replace("_", "-");
  return new Intl.NumberFormat(currentLocale, {
    style: "currency",
    currency: store.getters.CURRENCY_CODE
  }).format(price);
}

export function toPrintable(float_price, offset) {
  let currentLocale = store.getters.LOCALE_CODE.replace("_", "-");
  let prefix = "";
  if (typeof offset !== "undefined" && offset && float_price !== 0.0) {
    prefix = float_price > 0.0 ? "+" : "-";
  }
  let price = float_price;
  if (offset) {
    price = Math.abs(price);
  }
  let price_formatted = new Intl.NumberFormat(currentLocale, {
    style: "decimal",
    minimumFractionDigits: 2
  }).format(price);
  return prefix + price_formatted;
}

export function hideZero(price) {
  return ["0,00", "0.00", "0.-", "0,-"].includes(price) ? "" : price;
}

export function vatToStr(vat, default_vat) {
  if (vat === 0.0 || vat === null || vat === undefined) {
    vat = default_vat;
  }
  return new Intl.NumberFormat("en", {
    style: "decimal",
    minimumFractionDigits: 2
  }).format((vat - 1.0) * 100);
}

export function basketItemIsGroupPromotion(bi) {
  return (
    isCombo(bi) &&
    bi.meta_data &&
    bi.meta_data.extapp_promotion &&
    bi.meta_data.extapp_promotion.modifier_mode ==
      "replacement_price_in_cents" &&
    bi.meta_data.extapp_promotion.groups &&
    bi.meta_data.extapp_promotion.modifier_value
  );
}

export function recalculateGroupPromotionPrices(basket) {
  basket.forEach(bi => {
    if (isCombo(bi) && basketItemIsGroupPromotion(bi)) {
      let summed_single_original_prices = bi.components.reduce(
        (acc, mc) => parseFloat(mc.selected.original_price) + acc,
        0.0
      );

      console.log(
        "recalculateGroupPromotionPrices summed_single_original_prices: " +
          summed_single_original_prices
      );
      let modifier_value_cents = bi.meta_data.extapp_promotion.modifier_value;
      let target_menu_price = round(modifier_value_cents / 100, 2);
      console.log(
        "recalculateGroupPromotionPrices target_menu_price: " +
          target_menu_price
      );
      let pct_of_total = target_menu_price / summed_single_original_prices;

      bi.components.forEach(mc => {
        let product = mc.selected;
        console.log(
          "recalculateGroupPromotionPrices original_price: " +
            product.original_price
        );
        product.price = product.original_price * pct_of_total;
        product.fixed_price = product.original_price * pct_of_total;
      });
    }
  });
}

export function annotateBasketVats(basket, default_vat, global_coupons) {
  basket.forEach(bi => {
    let vats = {};

    if (isCombo(bi)) {
      bi.originalMenuPrice = comboPrice(bi, false, []) - comboDeposit(bi);

      // gross price with applied coupon
      let total_menu_price_vat_float =
        comboPrice(bi, true, global_coupons) - comboDeposit(bi);

      // TODO: remove legacy code/attrs after ronald dashboard shutdown
      // gross price without menu, sum of all single products
      let summed_single_product_prices = bi.components.reduce(
        (acc, mc) =>
          productPriceWithoutDeposit(mc.selected, false, [], 1) + acc,
        0.0
      );

      bi.components.forEach(mc => {
        let product = mc.selected;

        let pct_of_total =
          productPriceWithoutDeposit(product, false, [], 1) /
          summed_single_product_prices;

        // gross price of menu component
        let absolute_gross_of_menu = pct_of_total * total_menu_price_vat_float;

        let vat_str = vatToStr(product.vat, default_vat);
        let running_total = vats[vat_str] || 0.0;
        running_total += bi.count * absolute_gross_of_menu;
        vats[vat_str] = running_total;
      });

      annotateMenuVatBases(bi, bi.count, bi.count * total_menu_price_vat_float);
    } else {
      let vat_total = productPriceWithoutDeposit(bi, true, global_coupons, 0);
      vat_total +=
        productPriceWithoutDeposit(bi, true, global_coupons, 1) *
        (bi.count - 1);
      vats[vatToStr(bi.vat, default_vat)] = vat_total;

      annotateProductVatBases(bi, bi.count, vat_total);
    }
    bi.vats = {};
    for (const [key, value] of Object.entries(vats)) {
      bi.vats[key] = round(value, 2);
    }
  });

  const { lineItems, vatDetails } = finalizeBasketVatDetails(
    basket,
    global_coupons
  );

  basket.lineItems = lineItems;
  basket.vatDetails = vatDetails;
  basket.coupons = global_coupons;
}

export function itemSizePrice(size, product) {
  let ing_price = 0.0;
  if (!product.default_ingredients_included) {
    let bi = productToBasketItem(product);
    bi.selectedSize = size;
    ing_price = ingredientPrice(bi);
  }
  let item_price = toCalculatable(product.price);
  return item_price + ing_price;
}

export function sizeListItemPrice(size, menuOffset, product) {
  let price = toCalculatable(itemSizePrice(size, product));
  let offset = toCalculatable(size.offset_price);
  if (price + offset === 0.0) return "";
  if (menuOffset) {
    if (offset === 0.0) {
      return "";
    }
    return toPrintable(offset, true);
  }
  return toPrintable(price + offset);
}

export function extraListItemPrice(item) {
  if (item.soldout) {
    return Vue.i18n.translate("menu.soldout");
  }
  return hideZero(toPrintable(toCalculatable(item.offset_price), true));
}

export function productListItemPrice(item, menuOffset, activeCombo) {
  if (item.soldout) {
    return Vue.i18n.translate("menu.soldout");
  }
  let ing_price = 0.0;
  if (!item.default_ingredients_included) {
    ing_price = ingredientPrice(productToBasketItem(item));
  }
  let item_price = toCalculatable(
    menuOffset ? item.menu_offset_price : item.price
  );
  if (isPricingTypeSum(activeCombo)) {
    // always use price attribute if 'pricing_type' is sum
    item_price = toCalculatable(item.price);
  }
  let total_price = item_price + ing_price;
  if (total_price === 0.0) return "";
  let lowest_final_price = total_price;
  if (item.default_size) {
    var default_size = item.available_sizes.find(
      size => size.id === item.default_size
    );
    if (default_size)
      lowest_final_price =
        total_price + toCalculatable(default_size.offset_price);
  } else {
    if (item.available_sizes.length !== 0) {
      item.available_sizes.forEach(size => {
        if (
          total_price + toCalculatable(size.offset_price) <
          lowest_final_price
        ) {
          lowest_final_price = total_price + toCalculatable(size.offset_price);
        }
      });
    }
  }

  //let prefix = total_price !== lowest_final_price ? "vanaf " : "";
  //return `${prefix}${toPrintable(lowest_final_price, menuOffset)}`;
  return `${toPrintable(lowest_final_price, menuOffset)}`;
}
